import { useState } from 'react'
import { motion } from 'framer-motion'
import { Check, X, Zap, Star, Crown, Phone, Mail, Puzzle } from 'lucide-react'
import { toast } from 'react-toastify'
import Link from '../components/LocalizedLink'
import { useLanguage } from '../contexts/LanguageContext'
import SEO from '../components/SEO'

const translations = {
  el: {
    hero: {
      title: 'Απλή, Διαφανής Τιμολόγηση',
      subtitle: 'Επιλέξτε το πλάνο που ταιριάζει στο εργαστήριό σας. Ξεκινήστε με δωρεάν δοκιμή.',
      monthly: 'Μηνιαία',
      yearly: 'Ετήσια',
      save: 'Εξοικονόμηση',
    },
    plans: [
      {
        name: 'Δωρεάν Δοκιμή',
        description: 'Ιδανικό για να δοκιμάσετε το Workshop Assistant',
        features: ['5 ημέρες δωρεάν δοκιμή', 'Πλήρης πρόσβαση σε λειτουργίες', 'Εισαγωγή DXF & STEP', 'Χειροκίνητη δημιουργία εξαρτήματος', 'Επεξεργαστής παραμέτρων', 'Αναφορές Excel (μόνο σύνοψη)', 'Υποστήριξη email'],
        limitations: ['Λήγει μετά από 5 ημέρες', 'Χωρίς αναλυτικά φύλλα στο Excel'],
        cta: 'Κατεβάστε τη Δοκιμή',
      },
      {
        name: 'Επαγγελματικό',
        description: 'Για μικρά και μεσαία εργαστήρια',
        features: ['Έως 30 προσφορές το μήνα', 'Πλήρης πρόσβαση σε λειτουργίες', 'Πλήρεις αναφορές Excel με λεπτομέρειες', 'Εισαγωγή DXF & STEP', 'Χειροκίνητη δημιουργία εξαρτήματος', 'Επεξεργαστής παραμέτρων', 'Προτεραιότητα υποστήριξης email', 'Δωρεάν ενημερώσεις για 1 χρόνο'],
        limitations: [],
        cta: 'Ξεκινήστε Τώρα',
        popular: 'ΠΙΟ ΔΗΜΟΦΙΛΕΣ',
      },
      {
        name: 'Εταιρικό',
        description: 'Για μεγάλες κατασκευαστικές εγκαταστάσεις',
        features: ['Όλα του Επαγγελματικού', 'Απεριόριστες προσφορές', 'Πολλαπλές εγκαταστάσεις', 'Προσαρμοσμένες ενσωματώσεις', 'Αφιερωμένος διαχειριστής λογαριασμού', 'Διαθέσιμη εκπαίδευση στο χώρο', 'Υποστήριξη τηλεφώνου & email', 'Ισόβιες ενημερώσεις', 'Προσαρμοσμένες λειτουργίες κατόπιν αιτήματος'],
        limitations: [],
        cta: 'Ξεκινήστε Τώρα',
      },
      {
        name: 'Προσαρμοσμένο',
        description: 'Για νέες υλοποιήσεις ή διανομείς',
        features: ['Προσαρμοσμένη ανάπτυξη λειτουργιών', 'Ενσωμάτωση με υπάρχοντα συστήματα', 'Επωνυμία & ιδιωτική ετικέτα', 'Αφιερωμένη ομάδα υποστήριξης', 'Εκπαίδευση & ανάπτυξη', 'Τιμολόγηση βάσει όγκου για διανομείς', 'Συμφωνία επιπέδου υπηρεσιών (SLA)', 'Προτεραιότητα στις νέες λειτουργίες'],
        limitations: [],
        cta: 'Επικοινωνήστε μαζί μας',
      },
    ],
    perMonth: '/μήνα',
    perYear: '/έτος',
    billedAnnually: 'ανά μήνα, χρέωση ετήσια',
    whatsIncluded: 'Τι περιλαμβάνεται',
    processing: 'Επεξεργασία...',
    addons: {
      title: 'Πρόσθετα',
      inventor: {
        name: 'Autodesk Inventor Plugin',
        description: 'Εξάγετε δεδομένα εξαρτημάτων ελάσματος απευθείας από το Inventor assembly σας στο Workshop Assistant.',
        learnMore: 'Μάθετε περισσότερα',
        cta: 'Επικοινωνήστε για Αγορά',
      },
    },
    faq: {
      title: 'Συχνές Ερωτήσεις',
      items: [
        { q: 'Μπορώ να δοκιμάσω πριν αγοράσω;', a: 'Ναι! Κατεβάστε τη δωρεάν δοκιμή και χρησιμοποιήστε όλες τις λειτουργίες για 5 ημέρες. Δεν απαιτείται πιστωτική κάρτα.' },
        { q: 'Ποιες μεθόδους πληρωμής δέχεστε;', a: 'Δεχόμαστε όλες τις κύριες πιστωτικές κάρτες (Visa, Mastercard, American Express) και PayPal μέσω Stripe.' },
        { q: 'Μπορώ να αναβαθμίσω ή να υποβαθμίσω το πλάνο μου;', a: 'Ναι, μπορείτε να αλλάξετε το πλάνο σας ανά πάσα στιγμή. Επικοινωνήστε με την ομάδα υποστήριξης.' },
        { q: 'Ισχύει πολιτική επιστροφής χρημάτων;', a: 'Ναι, προσφέρουμε εγγύηση επιστροφής χρημάτων 30 ημερών.' },
        { q: 'Προσφέρετε εκπτώσεις για πολλαπλές άδειες;', a: 'Ναι! Επικοινωνήστε με την ομάδα πωλήσεων για τιμολόγηση όγκου για 5+ άδειες.' },
        { q: 'Πώς ενεργοποιώ την άδεια μου;', a: 'Μετά την αγορά, θα λάβετε σειριακό κωδικό μέσω email. Εισάγετέ τον στην εφαρμογή για ενεργοποίηση.' },
      ],
    },
    help: {
      title: 'Χρειάζεστε Βοήθεια για την Επιλογή;',
      subtitle: 'Η ομάδα μας είναι εδώ για να σας βοηθήσει να βρείτε το κατάλληλο πλάνο.',
      email: 'Email',
      phone: '+30 695 195 6643',
    },
  },
  en: {
    hero: {
      title: 'Simple, Transparent Pricing',
      subtitle: 'Choose the plan that fits your workshop. Start with a free trial.',
      monthly: 'Monthly',
      yearly: 'Yearly',
      save: 'Save',
    },
    plans: [
      {
        name: 'Free Trial',
        description: 'Perfect for trying out Workshop Assistant',
        features: ['5-day free trial', 'All features included', 'DXF & STEP import', 'Manual part creation', 'Parameters editor', 'Excel reports (summary only)', 'Email support'],
        limitations: ['Expires after 5 days', 'No detail sheets in Excel'],
        cta: 'Download Free Trial',
      },
      {
        name: 'Professional',
        description: 'For small to medium workshops',
        features: ['Up to 30 offers per month', 'All features included', 'Full Excel reports with details', 'DXF & STEP import', 'Manual part creation', 'Parameters editor', 'Priority email support', 'Free updates for 1 year'],
        limitations: [],
        cta: 'Get Started',
        popular: 'MOST POPULAR',
      },
      {
        name: 'Enterprise',
        description: 'For large fabrication shops',
        features: ['Everything in Professional', 'Unlimited offers', 'Multiple installations', 'Custom integrations', 'Dedicated account manager', 'On-site training available', 'Phone & email support', 'Lifetime updates', 'Custom features on request'],
        limitations: [],
        cta: 'Get Started',
      },
      {
        name: 'Custom',
        description: 'For new implementations or distributors',
        features: ['Custom feature development', 'Integration with existing systems', 'Branding & white-label options', 'Dedicated support team', 'Training & onboarding', 'Volume pricing for distributors', 'Service Level Agreement (SLA)', 'Priority on new features'],
        limitations: [],
        cta: 'Get in Touch',
      },
    ],
    perMonth: '/month',
    perYear: '/year',
    billedAnnually: 'per month, billed annually',
    whatsIncluded: "What's included",
    processing: 'Processing...',
    addons: {
      title: 'Add-ons',
      inventor: {
        name: 'Autodesk Inventor Plugin',
        description: 'Export sheet metal part data directly from your Inventor assembly into Workshop Assistant.',
        learnMore: 'Learn more',
        cta: 'Contact Us to Buy',
      },
    },
    faq: {
      title: 'Frequently Asked Questions',
      items: [
        { q: 'Can I try before I buy?', a: 'Yes! Download the free trial and use all features for 5 days. No credit card required.' },
        { q: 'What payment methods do you accept?', a: 'We accept all major credit cards (Visa, Mastercard, American Express) and PayPal through Stripe.' },
        { q: 'Can I upgrade or downgrade my plan?', a: 'Yes, you can change your plan at any time. Contact our support team for assistance.' },
        { q: 'Is there a refund policy?', a: "Yes, we offer a 30-day money-back guarantee. If you're not satisfied, contact us for a full refund." },
        { q: 'Do you offer discounts for multiple licenses?', a: 'Yes! Contact our sales team for volume pricing on 5+ licenses.' },
        { q: 'How do I activate my license?', a: "After purchase, you'll receive a serial key via email. Enter it in the application to activate." },
      ],
    },
    help: {
      title: 'Need Help Choosing?',
      subtitle: 'Our team is here to help you find the perfect plan for your workshop.',
      email: 'Email Us',
      phone: '+30 695 195 6643',
    },
  },
}

const planPrices = [
  { monthly: 0, yearly: 0 },
  { monthly: 45, yearly: 480, plan: { monthly: 'sheet_metal_estimator_monthly', yearly: 'sheet_metal_estimator_annual' } },
  { monthly: 79, yearly: 850, plan: { monthly: 'sheet_metal_estimator_enterprise_monthly', yearly: 'sheet_metal_estimator_enterprise_annual' } },
  { monthly: 'Custom', yearly: 'Custom' },
]

const inventorPrice = { monthly: 15, yearly: 150 }

const planMeta = [
  { id: 'trial', icon: <Zap className="h-8 w-8" />, ctaLink: '/download', color: 'gray' },
  { id: 'professional', icon: <Star className="h-8 w-8" />, popular: true, color: 'primary' },
  { id: 'enterprise', icon: <Crown className="h-8 w-8" />, color: 'secondary' },
  { id: 'custom', icon: <Phone className="h-8 w-8" />, ctaLink: '/contact', color: 'gray' },
]

const Pricing = () => {
  const [billingCycle, setBillingCycle] = useState('monthly')
  const [loading, setLoading] = useState(null)
  const { lang } = useLanguage()
  const t = translations[lang]
  const savings = Math.round((1 - (planPrices[1].yearly / (planPrices[1].monthly * 12))) * 100)

  const handlePurchase = async (index) => {
    const meta = planMeta[index]
    if (meta.ctaLink) { window.location.href = meta.ctaLink; return }
    const price = planPrices[index]
    if (!price.plan) { toast.error('This plan is not available for online purchase yet'); return }
    setLoading(meta.id)
    try {
      const response = await fetch(`${window.location.origin}/workshop_assistant/license/api/public/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan: price.plan[billingCycle] }),
      })
      const data = await response.json()
      if (!response.ok) throw new Error(data.detail || 'Something went wrong')
      window.location.href = data.url
    } catch (error) {
      toast.error(error.message || 'Something went wrong. Please try again.')
      setLoading(null)
    }
  }

  return (
    <div className="overflow-hidden">
      <SEO
        title="Pricing — Plans for Every Workshop"
        description="Simple, transparent pricing for Workshop Assistant. Compare plans and find the right fit for your sheet metal or CNC fabrication business."
        path="/pricing"
      />
      <section className="bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="section-container">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-5xl md:text-6xl font-bold mb-6">{t.hero.title}</motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-xl opacity-90 mb-8">{t.hero.subtitle}</motion.p>
            <div className="inline-flex items-center bg-gray-700 rounded-full p-1">
              <button onClick={() => setBillingCycle('monthly')} className={`px-6 py-2 rounded-full font-medium transition-all duration-200 ${billingCycle === 'monthly' ? 'bg-white text-gray-900' : 'text-gray-300 hover:text-white'}`}>{t.hero.monthly}</button>
              <button onClick={() => setBillingCycle('yearly')} className={`px-6 py-2 rounded-full font-medium transition-all duration-200 ${billingCycle === 'yearly' ? 'bg-white text-gray-900' : 'text-gray-300 hover:text-white'}`}>
                {t.hero.yearly}
                <span className="ml-2 text-xs bg-green-500 text-white px-2 py-1 rounded-full">{t.hero.save} {savings}%</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="section-container bg-gray-50">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
          {t.plans.map((plan, index) => {
            const meta = planMeta[index]
            const price = planPrices[index]
            return (
              <motion.div key={meta.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}
                className={`bg-white rounded-2xl shadow-xl overflow-hidden ${meta.popular ? 'ring-4 ring-primary-500 transform scale-105 lg:scale-110' : ''}`}>
                {meta.popular && <div className="bg-primary-600 text-white text-center py-2 font-semibold text-sm">{plan.popular}</div>}
                <div className="p-8">
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4 ${meta.color === 'primary' ? 'bg-primary-100 text-primary-600' : meta.color === 'secondary' ? 'bg-secondary-100 text-secondary-600' : 'bg-gray-100 text-gray-600'}`}>{meta.icon}</div>
                  <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                  <p className="text-gray-600 mb-6">{plan.description}</p>
                  <div className="mb-6">
                    {typeof price[billingCycle] === 'number' ? (
                      <>
                        <div className="flex items-baseline">
                          <span className="text-5xl font-bold">€{price[billingCycle]}</span>
                          {price[billingCycle] > 0 && <span className="text-gray-500 ml-2">{billingCycle === 'monthly' ? t.perMonth : t.perYear}</span>}
                        </div>
                        {billingCycle === 'yearly' && price.yearly > 0 && <div className="text-sm text-gray-500 mt-1">€{(price.yearly / 12).toFixed(2)} {t.billedAnnually}</div>}
                      </>
                    ) : (
                      <div className="text-5xl font-bold">{price[billingCycle]}</div>
                    )}
                  </div>
                  <button onClick={() => handlePurchase(index)} disabled={loading === meta.id}
                    className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-200 mb-8 ${meta.popular ? 'bg-primary-600 text-white hover:bg-primary-700' : 'bg-gray-100 text-gray-900 hover:bg-gray-200'} disabled:opacity-50 disabled:cursor-not-allowed`}>
                    {loading === meta.id ? t.processing : plan.cta}
                  </button>
                  <div className="space-y-4">
                    <div className="font-semibold text-sm text-gray-500 uppercase tracking-wide">{t.whatsIncluded}</div>
                    <ul className="space-y-3">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-start space-x-3">
                          <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                      {plan.limitations.map((limitation, i) => (
                        <li key={i} className="flex items-start space-x-3">
                          <X className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-500">{limitation}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            )
          })}
        </div>

        <div className="max-w-4xl mx-auto mt-20">
          <h2 className="text-3xl font-bold text-center mb-8">{t.addons.title}</h2>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="bg-white rounded-2xl shadow-xl p-8 flex flex-col md:flex-row md:items-center gap-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary-100 text-primary-600 flex-shrink-0"><Puzzle className="h-8 w-8" /></div>
            <div className="flex-1">
              <h3 className="text-2xl font-bold mb-2">{t.addons.inventor.name}</h3>
              <p className="text-gray-600 mb-2">{t.addons.inventor.description}</p>
              <Link to="/inventor-plugin" className="text-primary-600 font-medium hover:underline">{t.addons.inventor.learnMore} →</Link>
            </div>
            <div className="md:text-right flex-shrink-0">
              <div className="flex items-baseline md:justify-end">
                <span className="text-4xl font-bold">€{inventorPrice[billingCycle]}</span>
                <span className="text-gray-500 ml-2">{billingCycle === 'monthly' ? t.perMonth : t.perYear}</span>
              </div>
              {billingCycle === 'yearly' && <div className="text-sm text-gray-500 mt-1">€{(inventorPrice.yearly / 12).toFixed(2)} {t.billedAnnually}</div>}
              <Link to="/contact" className="inline-block mt-4 py-3 px-6 rounded-lg font-semibold bg-primary-600 text-white hover:bg-primary-700 transition-all duration-200">{t.addons.inventor.cta}</Link>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="section-container bg-white">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-12">{t.faq.title}</h2>
          <div className="space-y-6">
            {t.faq.items.map((faq, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="card">
                <h3 className="text-xl font-semibold mb-3">{faq.q}</h3>
                <p className="text-gray-600">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-container bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">{t.help.title}</h2>
          <p className="text-xl text-gray-600 mb-8">{t.help.subtitle}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="mailto:contact@automechlabs.com" className="flex items-center justify-center space-x-2 px-8 py-4 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-all duration-200">
              <Mail className="h-5 w-5" /><span>{t.help.email}</span>
            </a>
            <a href="tel:+306951956643" className="flex items-center justify-center space-x-2 px-8 py-4 bg-white border-2 border-primary-600 text-primary-600 rounded-lg font-semibold hover:bg-primary-50 transition-all duration-200">
              <Phone className="h-5 w-5" /><span>{t.help.phone}</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Pricing
